import { Link } from 'react-router-dom'
import { ArrowRight, Calculator, ClipboardList, Coins, Gauge, Ruler, ShieldCheck, Smartphone } from 'lucide-react'
import Seo, { SITE_URL } from '@/components/Seo'
import FaqList, { faqSchema } from '@/components/FaqList'
import SponsoredSlot from '@/components/SponsoredSlot'
import { FAQS } from '@/data/faqs'
import { POSTS, CATEGORIES, formatDate } from '@/data/blog'

const BENEFITS = [
  {
    icon: Coins,
    title: 'Know your budget first',
    text: 'Get a cost range with a line-by-line breakdown before you spend a cent on drawings or quotes.',
  },
  {
    icon: ClipboardList,
    title: 'A real materials list',
    text: 'Studs, rafters, sheathing, roofing, cladding, insulation and windows, all with order quantities and waste allowance.',
  },
  {
    icon: Gauge,
    title: 'Instant results',
    text: 'No sign-up and no waiting. Change any measurement and every number updates as you type.',
  },
  {
    icon: ShieldCheck,
    title: 'Transparent formulas',
    text: 'Every step is explained with worked examples, so you can verify the numbers or hand them to your builder.',
  },
  {
    icon: Smartphone,
    title: 'Built for the job site',
    text: 'Large touch targets and a fast mobile layout. Use it on the roof, in the attic or in the merchant queue.',
  },
  {
    icon: Ruler,
    title: 'Feet or metres',
    text: 'Switch units in one tap. Results convert instantly, with pitch handled as standard rise per 12 inches.',
  },
]

const STEPS = [
  { n: '1', title: 'Measure your roof space', text: 'Note the planned width, depth and front wall height of the dormer, plus the roof pitch.' },
  { n: '2', title: 'Enter the numbers', text: 'Type them into the calculator and choose a material finish from the dropdown.' },
  { n: '3', title: 'Read the estimate', text: 'See floor area, roof area, a materials list and a cost range with a full breakdown.' },
  { n: '4', title: 'Copy, share, refine', text: 'Copy the results for your builder, share a link, or change one value to compare options.' },
]

function Home() {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Dormer Calculator',
      url: SITE_URL,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Dormer Calculator',
      url: `${SITE_URL}/calculator`,
      applicationCategory: 'UtilitiesApplication',
      operatingSystem: 'Any (web browser)',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    },
    faqSchema(FAQS.slice(0, 5)),
  ]

  return (
    <>
      <Seo
        title="Dormer Calculator | Free Dormer Cost & Materials Estimator"
        description="Use our free dormer calculator to estimate dormer size, materials and construction cost. Enter width, depth, height, roof pitch and material for an instant result."
        path="/"
        schema={schema}
      />

      <section className="blueprint-grid text-white">
        <div className="container-page grid items-center gap-10 py-14 sm:py-20 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <p className="mb-4 inline-block rounded-full border border-blueprint-300/40 bg-white/5 px-3 py-1 text-xs font-bold uppercase tracking-widest text-blueprint-100">
              Free dormer calculator
            </p>
            <h1 className="text-4xl !text-white sm:text-5xl lg:text-6xl">
              Dormer Calculator: <span className="text-signal">size, materials and cost</span> in seconds
            </h1>
            <p className="mt-5 max-w-xl text-lg text-blueprint-100">
              Plan a dormer with confidence. Enter four measurements and a finish, and get a materials list, roof and wall areas, and a realistic construction cost range.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/calculator" className="btn btn-primary !px-7 !py-4 text-lg no-underline">
                <Calculator className="h-5 w-5" aria-hidden="true" />
                Open the dormer calculator
              </Link>
              <Link to="/blog" className="btn btn-outline-light no-underline">
                Read the cost guides
              </Link>
            </div>
            <p className="mt-4 text-sm text-blueprint-200">No sign-up. Works on phone, tablet and desktop.</p>
          </div>

          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1763979021741-cfbe06355d7a?auto=format&fit=crop&w=900&q=70"
              alt="Dormer window on a shingled roof"
              width={900}
              height={600}
              {...{ fetchpriority: 'high' }}
              className="aspect-[4/3] w-full rounded-2xl object-cover shadow-lift ring-1 ring-white/20"
            />
            <div className="absolute -bottom-5 left-4 right-4 rounded-xl bg-white p-4 text-ink shadow-lift sm:left-auto sm:right-[-12px] sm:w-64">
              <p className="text-xs font-bold uppercase tracking-wide text-steel-500">Example: 12 x 8 ft shed dormer</p>
              <p className="text-2xl font-extrabold">About $39,000</p>
              <p className="text-xs text-steel-500">Fiber cement finish, 4:12 pitch</p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-page py-16 sm:py-20" aria-labelledby="benefits-heading">
        <p className="eyebrow">Why use it</p>
        <h2 id="benefits-heading" className="mt-2 max-w-2xl text-3xl sm:text-4xl">
          The fastest way to put numbers on a dormer project
        </h2>
        <div className="mt-10 grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map(b => (
            <div key={b.title} className="flex gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blueprint-50 text-blueprint-700">
                <b.icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <div>
                <h3 className="text-lg">{b.title}</h3>
                <p className="mt-1 text-steel-600">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-steel-200 bg-white py-16 sm:py-20" aria-labelledby="how-heading">
        <div className="container-page">
          <p className="eyebrow">How to use it</p>
          <h2 id="how-heading" className="mt-2 text-3xl sm:text-4xl">
            How to calculate a dormer in four steps
          </h2>
          <ol className="mt-10 grid gap-6 md:grid-cols-4">
            {STEPS.map(s => (
              <li key={s.n} className="relative rounded-2xl border border-steel-200 bg-steel-50 p-5">
                <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-ink text-lg font-extrabold text-signal">
                  {s.n}
                </span>
                <h3 className="text-lg">{s.title}</h3>
                <p className="mt-1 text-sm text-steel-600">{s.text}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8">
            <Link to="/calculator" className="btn btn-dark no-underline">
              Start calculating <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-page py-16 sm:py-20" aria-labelledby="what-heading">
        <div className="grid gap-10 lg:grid-cols-[2fr_1fr]">
          <div>
            <h2 id="what-heading" className="text-3xl sm:text-4xl">
              What is a dormer calculator?
            </h2>
            <div className="mt-4 space-y-4 text-lg">
              <p>
                A dormer calculator turns the size of a planned dormer into practical numbers: how much roof, wall and floor you are building, which materials to order and what the work is likely to cost. It replaces a page of manual geometry with one form.
              </p>
              <p>
                Dormers add headroom, light and floor space to a loft without changing the footprint of the house. Because they are built on top of an existing roof, costs depend on roof pitch, wall height and finish as much as width. Our dormer calculator accounts for each of these, with 10 percent waste, 10 percent contingency and design and permit costs included.
              </p>
              <p>
                Use it to compare a shed dormer with a gable dormer, test different finishes, or sense-check a contractor quote. For deeper background, see our{' '}
                <Link to="/blog/cost-of-dormer-window" className="font-semibold underline">dormer cost guide</Link> and{' '}
                <Link to="/blog/shed-vs-gable-dormer" className="font-semibold underline">shed vs gable dormer comparison</Link>.
              </p>
            </div>
          </div>
          <div className="lg:pt-2">
            <SponsoredSlot />
          </div>
        </div>
      </section>

      <section className="border-t border-steel-200 bg-white py-16 sm:py-20" aria-labelledby="blog-heading">
        <div className="container-page">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow">From the blog</p>
              <h2 id="blog-heading" className="mt-2 text-3xl sm:text-4xl">Dormer guides and cost advice</h2>
            </div>
            <Link to="/blog" className="font-bold">All articles &rarr;</Link>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {POSTS.slice(0, 3).map(p => (
              <article key={p.slug} className="card overflow-hidden transition-shadow hover:shadow-lift">
                <img src={p.image.replace('w=1200', 'w=600')} alt={p.imageAlt} width={600} height={400} loading="lazy" className="aspect-[3/2] w-full object-cover" />
                <div className="p-5">
                  <p className="text-xs font-bold uppercase tracking-wide text-blueprint-600">
                    {CATEGORIES.find(c => c.slug === p.category)?.name} &middot; {formatDate(p.datePublished)}
                  </p>
                  <h3 className="mt-2 text-lg">
                    <Link to={`/blog/${p.slug}`} className="text-ink no-underline hover:underline">{p.title}</Link>
                  </h3>
                  <p className="mt-2 text-sm text-steel-600">{p.excerpt}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page py-16 sm:py-20" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl">
          <h2 id="faq-heading" className="text-3xl sm:text-4xl">Dormer calculator FAQ</h2>
          <div className="mt-8">
            <FaqList items={FAQS.slice(0, 5)} />
          </div>
          <p className="mt-6">
            <Link to="/faq" className="font-bold">See all questions &rarr;</Link>
          </p>
        </div>
      </section>

      <section className="bg-ink py-14 text-center">
        <div className="container-page">
          <h2 className="text-3xl !text-white sm:text-4xl">Ready to price your dormer?</h2>
          <p className="mx-auto mt-3 max-w-xl text-blueprint-100">It takes under a minute and costs nothing.</p>
          <Link to="/calculator" className="btn btn-primary mt-6 !px-8 !py-4 text-lg no-underline">
            Use the dormer calculator <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </>
  )
}

export default Home
